// This is the config file for all the events to balance game play and adjust the player narrative.
// await - wait for the focus (or something else) to finish

import { getLast, getRandom } from 'helpers';
import { ants } from './ants';
import { foods } from './foods';

export const events: GameEventsSetup = {
    // Chapter 1
    intro: {
        onTrigger: async (gameState) => {
            gameState.getEntity('nature', 'antColony').highlight();
            await gameState.showPopup('intro');
            gameState.sounds.play('unlock');
        },
    },
    unlockAntWorker: {
        after: ['intro'],
        onTrigger: (gameState) => {
            gameState.createEntity('food', 'bread1', foods.bread1); // First piece of food is just out of sight.
            gameState.setUnlock('colonyHealth', true); // unlock health from game start.
            gameState.setUnlock('currentRate', true);
            gameState.setUnlock('starvation', true);
            gameState.setUnlock('antWorker', true);
            void gameState.showPopup('unlockAntWorker');
        },
    },
    createdFirstAntWorker: { // Once one ant is draining calories, explain starvation.
        after: ['unlockAntWorker'],
        condition: (gameState) => gameState.entities.ants.workers.created === 1,
        delay: 2,
        onTrigger: (gameState) => gameState.showPopup('explainHealth'),
    },
    builtTooFast: { // If player creates a few ants, give them a warning.
        after: ['createdFirstAntWorker'],
        condition: (gameState, prevState) => gameState.calories < 100 && gameState.entities.ants.workers.created > prevState.entities.ants.workers.created,
        onTrigger: (gameState) => gameState.showPopup('builtTooFast'),
    },
    foundFirstFood: {
        after: ['createdFirstAntWorker'],
        condition: (gameState) => gameState.getEntity('food')?.found,
        removes: ['builtTooFast'],
        onTrigger: async (gameState) => {
            const food = gameState.getEntity('food');
            await gameState.focusEntity(food, 1.2, false, true);
            gameState.sounds.play('discovery', 0.5);
            await gameState.showPopup('foundFirstFood');
            void gameState.focusEntity(food, 0.8);
            void gameState.takeSnapshot();
        },
    },
    wontCreateAnotherAntWorker: { // Prompt the user to create another ant if they don't do anything in a few turns.
        after: ['foundFirstFood'],
        condition: (gameState) => gameState.entities.ants.workers.created === 1 && gameState.turns === 30,
        onTrigger: (gameState) => gameState.showPopup('wontCreateAnotherAntWorker'),
    },
    wontControlMap: { // Tell user how to use the zoom and pan controls
        after: ['foundFirstFood'],
        condition: (gameState) => !gameState.map.drag,
        delay: 20,
        doubleCheck: true,
        onTrigger: (gameState) => gameState.showPopup('wontControlMap'),
    },
    starvation: {
        condition: (gameState) => gameState.calories === 0,
        onTrigger: (gameState) => gameState.showPopup('starvation'),
    },
    retry: { // This allows the user to restart from the last saved snapshot.
        after: ['foundFirstFood'],
        condition: (gameState) => gameState.entities.ants.active.length === 0 && gameState.calories <= (ants.worker.creationCalories || 0),
        repeat: true,
        onTrigger: async (gameState) => {
            await gameState.showPopup('retry');
            void gameState.retry();
        },
    },

    // Chapter 2
    // Add reserve limit once the food is gone and the threat of starvation is real.
    consumedFirstFood: {
        after: ['createdFirstAntWorker'],
        condition: (gameState) => gameState.entities.foods.removed === 1,
        delay: 1,
        onTrigger: (gameState) => {
            gameState.removeEntity('food', 'bread1');
            void gameState.showPopup('consumedFirstFood');
            gameState.createEntity('food', 'cake', foods.cake);
        },
    },
    reserveLimitUnlocked: {
        after: ['consumedFirstFood'],
        delay: 1,
        onTrigger: (gameState) => {
            gameState.sounds.play('unlock');
            void gameState.showPopup('reserveLimitUnlocked');
            gameState.setUnlock('reserveLimit', true);
        },
    },
    reserveLimitAdjusted: {
        after: ['consumedFirstFood'],
        condition: (gameState) => gameState.caloriesReserve > 0,
        delay: 20,
        doubleCheck: true,
        removes: ['reserveLimitUntouched'],
    },
    reserveLimitUntouched: {
        after: ['consumedFirstFood'],
        condition: (gameState) => gameState.caloriesReserve === 0,
        delay: 50,
        doubleCheck: true,
        removes: ['reserveLimitAdjusted'],
    },
    foundBigFood: {
        after: ['consumedFirstFood'],
        condition: (gameState) => gameState.getEntity('food')?.found,
        onTrigger: async (gameState) => {
            const food = gameState.getEntity('food');
            gameState.sounds.play('discovery', 0.5);
            await gameState.focusEntity(food, 1.1);
            void gameState.showPopup('foundBigFood');
            await gameState.focusEntity(food, 0.9);
            void gameState.takeSnapshot();
        },
    },

    // Chapter 3
    createDangerColony: {
        after: ['foundBigFood'],
        condition: (gameState) => gameState.turns >= 100 || gameState.entities.ants.workers.active.length >= 10 || gameState.getEntity('food')?.remaining <= 0.99,
        delay: 20,
    },
    matchAntWorkers: {
        after: ['createDangerColony'],
        condition: (gameState) => gameState.entities.dangers.antWorkers.active.length < gameState.entities.ants.workers.active.length,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWorker'),
    },
    spottedDangerAnts: {
        after: ['createDangerColony'],
        condition: (gameState) => gameState.entities.dangers.antWorkers.active.some(ant => ant.targetEngaged),
        delay: 3,
        onTrigger: async (gameState) => {
            await gameState.focusEntity(gameState.getEntity('food'), 1);
            gameState.sounds.play('danger-ants', 1);
            void gameState.showPopup('spottedDangerAnts');
        },
    },
    createDangerAntWarrior: {
        after: ['createDangerColony'],
        delay: 15,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWarrior'),
    },
    firstAntWorkerAttacked: {
        after: ['createDangerAntWarrior'],
        condition: (gameState) => gameState.getEntity('danger', 'antWarrior')?.targetEngaged,
        delay: 1,
        onTrigger: async (gameState) => {
            await gameState.focusEntity(gameState.getEntity('danger', 'antWarrior'), 1.3);
            void gameState.showPopup('firstAntWorkerAttacked');
        },
    },
    unlockAntWarrior: {
        after: ['firstAntWorkerAttacked'],
        delay: 2,
        onTrigger: (gameState) => {
            gameState.sounds.play('unlock');
            gameState.setUnlock('antWarrior', true);
            void gameState.showPopup('unlockAntWarrior');
            void gameState.takeSnapshot();
        },
    },
    // Maintain balance of power
    createDangerAntWarriorOnIdle: {
        after: ['unlockAntWarrior'],
        condition: (gameState, _prevState, afterState) => gameState.entities.ants.warriors.active.length === afterState.entities.ants.warriors.active.length && gameState.entities.dangers.antWarriors.active.length < (gameState.entities.ants.warriors.active.length + 1),
        interval: 20,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWarrior'),
    },
    preventEarlyWin: {
        after: ['unlockAntWarrior'],
        condition: (gameState) => gameState.calories > 2000,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWarrior'),
    },
    addDangerAntWarriors: { // Prevent player from getting too far ahead of the enemy.
        after: ['unlockAntWarrior'],
        condition: (gameState) => gameState.entities.dangers.antWarriors.active.length < gameState.entities.ants.warriors.active.length,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWarrior'),
    },
    reduceDangerAntWarriors: { // Prevent player from getting too far behind the enemy.
        after: ['unlockAntWarrior'],
        condition: (gameState) => gameState.entities.dangers.antWarriors.active.length > gameState.entities.ants.warriors.active.length + 3 && gameState.calories < 2000,
        repeat: true,
        onTrigger: (gameState) => {
            const antWarrior = gameState.entities.dangers.antWarriors.active.find(antWarrior => antWarrior.isInView());
            gameState.removeEntity('danger', 'antWarrior', antWarrior);
        },
    },

    // Chapter 4
    unlockPortfolioTool: {
        after: ['unlockAntWarrior'],
        condition: (gameState, _prevState, afterState) => gameState.turns - afterState.turns >= 100 || gameState.entities.ants.active.length > 30 || gameState.entities.ants.warriors.active.length > 4,
        onTrigger: (gameState) => {
            gameState.sounds.play('unlock');
            gameState.setUnlock('portfolioTool', true);
            void gameState.showPopup('unlockPortfolioTool');
            void gameState.takeSnapshot();
        },
    },
    nudgePortfolioTool: { // Nudge the user to modify their portfolio allocation.
        after: ['unlockPortfolioTool'],
        condition: (gameState, _prevState, afterState) => gameState.turns - afterState.turns >= 25 && gameState.entities.ants.allocation === 0,
        delay: 1,
        onTrigger: (gameState) => gameState.showPopup('nudgePortfolioTool'),
    },

    // Chapter 5
    createDangerAnteater: {
        after: ['unlockPortfolioTool'],
        condition: (gameState) => gameState.entities.ants.allocation > 0,
        removes: ['nudgePortfolioTool'],
        delay: 20,
        onTrigger: (gameState) => gameState.createEntity('danger', 'anteater', { killsTarget: (gameState.entities.ants.active.length + gameState.entities.dangers.active.length) * 0.5, x: 0, y: 18 }),
    },
    focusDangerAnteater: { // Focus on anteater after its first kill
        after: ['createDangerAnteater'],
        condition: (gameState) => gameState.getEntity('danger', 'anteater')?.kills > 0,
        onTrigger: async (gameState) => {
            await gameState.focusEntity(gameState.getEntity('danger', 'anteater'), 1);
            gameState.sounds.play('danger-anteater');
            void gameState.showPopup('focusDangerAnteater');
            gameState.getEntity('food', 'cake')?.setCalories(2000);
        },
    },
    createRandomFoods: {
        after: ['focusDangerAnteater'],
        preserve: true,
        onTrigger: (gameState) => {
            // Add random foods, only if cell is in fog
            foods.random.forEach(foodProp => {
                if (gameState.fog[`${foodProp.x},${foodProp.y}`]) {
                    gameState.createEntity('food', foodProp.type as FoodType, foodProp);
                }
            });
        },
    },
    unlockAntExplorer: {
        after: ['focusDangerAnteater'],
        delay: 5,
        onTrigger: (gameState) => {
            gameState.sounds.play('unlock');
            gameState.setUnlock('antExplorer', true);
            void gameState.showPopup('unlockAntExplorer');
            void gameState.takeSnapshot();
        },
    },
    removeDangerAnteater: { // Remove anteater when it has killed half the amount of ants
        after: ['createDangerAnteater'],
        condition: (gameState) => gameState.getEntity('danger', 'anteater')?.killsReached,
        onTrigger: (gameState) => {
            gameState.getEntity('danger', 'anteater').wanderOff(25, 49);

            // Add bread2 in closest unrevealed cell
            const antColony = gameState.getEntity('nature', 'antColony');
            const cell = antColony.getClosestFogCell();
            if (cell) {
                gameState.createEntity('food', 'bread2', { ...foods.bread2, x: cell.x, y: cell.y });
            }
        },
    },
    createdFirstAntExplorer: {
        after: ['unlockAntExplorer'],
        condition: (gameState) => gameState.entities.ants.explorers.created === 1,
        onTrigger: (gameState) => {
            gameState.createEntity('danger', 'spider');
            void gameState.focusEntity(getLast(gameState.entities.ants.explorers.active), 0.7, true);
        },
    },
    distractDangerAnts: { // This will create foods near the enemy so the player isn't constantly attacked.
        after: ['unlockAntExplorer'],
        condition: (gameState) => gameState.entities.foods.active[0]?.type !== 'cake',
        onTrigger: (gameState) => {
            gameState.createEntity('food', 'bread2', foods.bread3);
            gameState.createEntity('food', 'redgrapes', foods.redgrapes);
            // gameState.createEntity('food', 'applepie', foods.applepie);
            // gameState.createEntity('food', 'watermelon', foods.watermelon);
        },
    },
    firstAntExplorerDeath: {
        after: ['createdFirstAntExplorer'],
        condition: (gameState) => gameState.entities.ants.explorers.active[0]?.index !== 1,
        onTrigger: async (gameState) => {
            await gameState.focusEntity(getLast(gameState.entities.dangers.spiders.active), 1);
            await gameState.showPopup('firstAntExplorerDeath');
            gameState.setUnlock('antExplorerMutation', true);
        },
    },
    magicFood:{ // Create food in a fogged cell when the player is in trouble, has run out of food within maxDistance (range), and has not found the cheese yet.
        after: ['unlockAntExplorer'],
        condition: (gameState, prevState) => gameState.entities.foods.active.length !== prevState.entities.foods.active.length && !gameState.unlocks.river && !gameState.getEntity('nature', 'antColony').getFoodsInRange(),
        repeat: true,
        onTrigger: (gameState) => {
            const antColony = gameState.getEntity('nature', 'antColony');
            const cell = antColony.getClosestFogCell();
            if (cell) {
                gameState.createEntity('food', 'bread2', { ...foods.bread4, x: cell.x, y: cell.y });
            }
        }
    },
    noFoodInRange: {
        after: ['magicFood'],
        condition: (gameState, prevState) => gameState.entities.foods.active.length !== prevState.entities.foods.active.length && !gameState.getEntity('nature', 'antColony').getFoodsInRange(),
        onTrigger: (gameState) => gameState.showPopup('noFoodInRange'),
    },
    createdGoodAntExplorer: {
        after: ['firstAntExplorerDeath'],
        condition: (gameState) => gameState.entities.ants.explorers.mutations === 1,
        onTrigger: async (gameState) => {
            gameState.setUnlock('antExplorerMutation', false);
            await gameState.focusEntity(getLast(gameState.entities.ants.explorers.active), 1);
            await gameState.showPopup('createdGoodAntExplorer');
            gameState.setUnlock('antExplorerMutation', true);
        },
    },
    createdBadAntExplorer: {
        after: ['createdGoodAntExplorer'],
        condition: (gameState) => gameState.entities.ants.explorers.mutations === 2,
        onTrigger: async (gameState) => {
            gameState.setUnlock('antExplorerMutation', false);
            await gameState.focusEntity(getLast(gameState.entities.ants.explorers.active), 1);
            await gameState.showPopup('createdBadAntExplorer');
            gameState.setUnlock('antExplorerMutation', true);
        },
    },
    createdParadiseAntExplorer: {
        after: ['createdBadAntExplorer'],
        condition: (gameState, prevState) => gameState.unlocks.antExplorerMutation && gameState.entities.ants.explorers.created > prevState.entities.ants.explorers.created,
        onTrigger: (gameState) => {
            gameState.setUnlock('antExplorerMutation', false);
            gameState.createEntity('food', 'cheese', foods.cheese);
            void gameState.focusEntity(getLast(gameState.entities.ants.explorers.active), 1.2, true);
        },
    },
    foundParadise: {
        after: ['createdParadiseAntExplorer'],
        condition: (gameState) => gameState.unlocks.river,
        onTrigger: async (gameState) => {
            const cheese = gameState.getEntity('food', 'cheese');
            gameState.revealFog(cheese.cell, 8);
            await gameState.focusEntity(cheese, 1);
            gameState.sounds.play('discovery', 0.5);
            await gameState.showPopup('foundParadise');
            await gameState.focusEntity(cheese, 0.8);
            gameState.setUnlock('antExplorerMutation', true);
        },
    },

    // Chapter 6
    // Path 6 A
    createdBuffAntExplorer: {
        after: ['foundParadise'],
        condition: (gameState, prevState) => gameState.entities.ants.explorers.created > prevState.entities.ants.explorers.created && getLast(gameState.entities.ants.explorers.active)?.setup.buff,
        repeat: true,
        onTrigger: async (gameState, prevState) => {
            const explorer = getLast(gameState.entities.ants.explorers.active);
            const iteration = prevState.entities.ants.buffs[explorer.setup.buff] ? 'another' : 'first';
            if (iteration === 'first') {
                await gameState.focusEntity(explorer, 1);
            }
            switch (`${iteration}-${explorer.setup.buff}`) {
                case 'first-attack': void gameState.showPopup('createdAttackBuffAntExplorer'); break;
                case 'first-defense': void gameState.showPopup('createdDefenseBuffAntExplorer'); break;
                case 'first-speed': void gameState.showPopup('createdSpeedBuffAntExplorer'); break;
                case 'first-carry': void gameState.showPopup('createdCarryBuffAntExplorer'); break;
                case 'another-attack': void gameState.showPopup('createdAnotherAttackBuffAntExplorer'); break;
                case 'another-defense': void gameState.showPopup('createdAnotherDefenseBuffAntExplorer'); break;
                case 'another-speed': void gameState.showPopup('createdAnotherSpeedBuffAntExplorer'); break;
                case 'another-carry': void gameState.showPopup('createdAnotherCarryBuffAntExplorer'); break;
            }
        },
    },
    // Path 6 B
    createRandomDangerOnIdle: {
        after: ['foundParadise'],
        interval: 200,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', getRandom(['anteater', 'lawnmower']), { life: 100 }),
    },
    // Path 6 C
    createDangerAntwarriorForHighIncome: {
        after: ['foundParadise'],
        condition: (gameState) => gameState.caloriesIncome >= 50,
        interval: 5,
        repeat: true,
        onTrigger: (gameState) => gameState.createEntity('danger', 'antWarrior'),
    },
    // Merge paths
    wontCreateAnotherAntExplorer: { // Prompt the user to create another ant if they don't do anything in a 30 turns.
        after: ['foundParadise'],
        condition: (gameState, _prevState, afterState) => gameState.entities.ants.explorers.created <= 5 && gameState.turns - afterState.turns >= 30,
        onTrigger: (gameState) => gameState.showPopup('wontCreateAnotherAntExplorer'),
    },

    // Finale
    createdGardeningAntExplorer: {
        after: ['createdFirstAntExplorer'],
        condition: (gameState) => gameState.getEntity('ant', 'explorer', 'gardening'),
        removes: ['createRandomDangerOnIdle'],
    },
    buildingParadiseBridge: {
        after: ['createdGardeningAntExplorer'],
        condition: (gameState) => gameState.getEntity('ant', 'explorer', 'gardening')?.target,
        delay: 1,
        onTrigger: async (gameState) => {
            const gardeningTarget = gameState.getEntity('ant', 'explorer', 'gardening').target;
            await gameState.focusEntity(gardeningTarget, 1);
            await gameState.showPopup('buildingParadiseBridge');
            await gameState.focusEntity(gardeningTarget, 0.8);
        },
    },
    createDangerMagnifier: {
        after: ['buildingParadiseBridge'],
        delay: 10,
        onTrigger: (gameState) => gameState.createEntity('danger', 'magnifier'),
    },
    unlockParadise: {
        after: ['createdGardeningAntExplorer'],
        condition: (gameState) => !gameState.entities.natures.active.some(nature => nature.type === 'bridgeBlocker'),
        onTrigger: async (gameState) => {
            gameState.setUnlock('antExplorer', false);
            gameState.setUnlock('antWarrior', false);
            gameState.setUnlock('antWorker', false);
            gameState.setUnlock('colonyHealth', false);
            gameState.setUnlock('currentRate', false);
            gameState.setUnlock('portfolioTool', false);
            gameState.setUnlock('starvation', false);
            gameState.setUnlock('paradise', true);
            const bridgeLeaf = gameState.entities.natures.active.find(nature => !nature.active && nature.type.startsWith('leaf'));
            await gameState.focusEntity(bridgeLeaf, 1);
            await gameState.showPopup('unlockParadise');
            const queen = gameState.createEntity('ant', 'queen');
            void gameState.focusEntity(queen, 0.7, true);
        },
    },
    victory: {
        after: ['unlockParadise'],
        condition: (gameState) => gameState.getEntity('ant', 'queen')?.atParadise,
        delay: 1,
        doubleCheck: true,
        onTrigger: async (gameState) => {
            await gameState.focusEntity(gameState.getEntity('food', 'cheese'), 1);
            gameState.sounds.play('victory');
            void gameState.showPopup('victory');
            gameState.finish();
        },
    },

};
